import {
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grow,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material"
import { useContext, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import EditContainer from "../../components/global/editContainer"
import PageSpinner from "../../components/global/pageSpinner"
import { MainContext } from "../../controllers/main"
import { deepCopy, DialogTransition } from "../../services/utilities/utility"
import { LanguagesContext } from "../../controllers/languages"
import { AddCircleRounded } from "@mui/icons-material"
import LoadingBackdrop from "../../components/global/loadingBackdrop"
import CantPerformActionDialog from "../../components/global/cantPerformActionDialog"
import EditHeadingWithStages from "../../components/global/editHeadingWithStages"
import { Stage } from "../../services/config/enum"
import { MissionsContext } from "../../controllers/missions"
import MissionGeneralEdit from "../../components/mission/missionGeneralEdit"
import MissionTranslationsEdit from "../../components/mission/missionTranslationsEdit"

const MissionEdit = () => {
  const { animation, setAnimation, setChangesSaved, showPadding } =
    useContext(MainContext)
  const { languages, languagesForEpisodeTabs } = useContext(LanguagesContext)
  const {
    loading: controllerLoading,
    setLoading: setControllerLoading,
    editMode,
    setEditMode,
    doneChanges,
    cancelChanges,
    currentMission,
    getCurrentMission,
    preChangesCurrentMission,
    addTranslation,
    hasError,
    upsertMissionParent,
    upsertMissionDocument,
    publishMission,
  } = useContext(MissionsContext)
  const { missionId } = useParams()
  const navigate = useNavigate()

  // prevent tab close
  useEffect(() => {
    const handleTabClose = (event: any) => {
      event.preventDefault()
      console.log("beforeunload event triggered")
      return (event.returnValue = "Are you sure you want to exit?")
    }

    window.addEventListener("beforeunload", handleTabClose)

    return () => {
      window.removeEventListener("beforeunload", handleTabClose)
    }
  }, [])

  // loading
  const [loading, setLoading] = useState<boolean>(true)

  // page error
  const [pageError, setPageError] = useState<boolean>(false)

  // fetch current product
  const fetchAll = async () => {
    const noErrors = await getCurrentMission(missionId)
    if (noErrors) {
      setLoading(false)
      setControllerLoading(false)
    } else {
      setPageError(true)
    }
  }

  useEffect(() => {
    fetchAll()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // tabs
  const [selectedTab, setSelectedTab] = useState<number>(0)

  // add translation dialog
  const [addTranslationOpen, setAddTranslationOpen] = useState<boolean>(false)
  const [translationToAdd, setTranslationToAdd] = useState<
    { value: string; label: string; flag?: string } | undefined
  >()

  const handleAddTranslationClose = () => {
    setAddTranslationOpen(false)
    setTimeout(() => {
      setTranslationToAdd(undefined)
    }, 150)
  }

  // no default translation alert dialog
  const [
    noDefaultTranslationAlertDialogOpen,
    setNoDefaultTranslationAlertDialogOpen,
  ] = useState<boolean>(false)

  // suspend mission
  const [suspendDialogOpen, setSuspendDialogOpen] = useState<boolean>(false)

  return loading ? (
    <PageSpinner
      showBackButton={pageError}
      backButtonOnClick={() => {
        setAnimation(false)
        navigate("/missions")
      }}
    />
  ) : (
    <EditContainer>
      <EditHeadingWithStages
        saveButtonLoading={controllerLoading}
        backButtonOnClick={() => {
          setAnimation(false)
          setTimeout(() => {
            navigate("/missions")
          }, 300)
        }}
        title={
          preChangesCurrentMission.document.items.find(
            (documentItem) => documentItem.isDefault
          ).title
        }
        id={preChangesCurrentMission.id}
        editModeActive={editMode}
        setEditModeActive={setEditMode}
        saveButtonDisabled={
          hasError || (currentMission.stage === Stage.PUBLISHED && !doneChanges)
        }
        undoButtonDisabled={!doneChanges}
        doneChanges={doneChanges}
        undoButtonOnClick={() => {
          if (selectedTab > preChangesCurrentMission.document.items.length) {
            setSelectedTab(preChangesCurrentMission.document.items.length)
          }
          cancelChanges()
        }}
        saveButtonOnClick={async () => {
          if (!doneChanges && currentMission.stage === Stage.DRAFT) {
            setControllerLoading(true)
            const noErrors = await publishMission()

            if (noErrors) {
              setControllerLoading(false)
              setChangesSaved(true)
            } else {
              setControllerLoading(false)
            }

            return
          }

          if (
            currentMission.document.items.filter((item) => item.isDefault)
              .length
          ) {
            setControllerLoading(true)
            let noErrors = await upsertMissionParent(currentMission.suspended)
            if (noErrors) {
              noErrors = await upsertMissionDocument()
              if (noErrors) {
                setControllerLoading(false)
                setChangesSaved(true)
              } else {
                setControllerLoading(false)
              }
            } else {
              setControllerLoading(false)
            }
          } else {
            setNoDefaultTranslationAlertDialogOpen(true)
          }
        }}
        stage={doneChanges ? Stage.EDITED : currentMission.stage}
        showSuspendButton
        isSuspended={currentMission.suspended}
        suspendButtonOnClick={() => {
          setSuspendDialogOpen(true)
        }}
      />
      <Grow
        in={animation}
        timeout={300}
        style={{
          margin: showPadding === "yes" ? 16 : 0,
          marginTop: 0,
        }}
      >
        <Stack>
          {/* languages tabs */}
          <Stack direction="row" justifyContent="flex-start">
            <Card
              variant="outlined"
              style={{
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                borderTopLeftRadius: showPadding === "yes" ? null : 0,
                borderBottom: "none",
                borderLeft: showPadding === "yes" ? null : "none",
              }}
            >
              <Stack direction="row" justifyContent="flex-start">
                <Tabs
                  value={selectedTab}
                  onChange={(e: any, newValue: number) => {
                    setSelectedTab(newValue)
                  }}
                  aria-label="languages tabs"
                >
                  <Tab label="General" style={{ minWidth: 180 }} />
                  {currentMission.document.items.map((item) => (
                    <Tab
                      key={item.lang}
                      style={{ minWidth: 180 }}
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>
                            {
                              languagesForEpisodeTabs.find(
                                (arrayItem) => arrayItem.value === item.lang
                              ).flag
                            }
                          </span>
                          {currentMission.document.items.find(
                            (documentItem) => documentItem.lang === item.lang
                          ).isDefault ? (
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              <span>
                                {
                                  languagesForEpisodeTabs.find(
                                    (arrayItem) => arrayItem.value === item.lang
                                  ).label
                                }
                              </span>
                              <span style={{ fontSize: 10, marginTop: 2 }}>
                                (Default)
                              </span>
                            </Stack>
                          ) : (
                            <span>
                              {
                                languagesForEpisodeTabs.find(
                                  (arrayItem) => arrayItem.value === item.lang
                                ).label
                              }
                            </span>
                          )}
                        </Stack>
                      }
                    />
                  ))}
                </Tabs>
                <Divider orientation="vertical" variant="middle" flexItem />
                <div
                  style={{
                    minWidth: 70,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <IconButton
                    disabled={!editMode}
                    aria-label="add language button"
                    component="span"
                    style={{ width: 48, height: 48 }}
                    onClick={() => {
                      setAddTranslationOpen(true)
                    }}
                  >
                    <AddCircleRounded />
                  </IconButton>
                </div>
              </Stack>
            </Card>
          </Stack>
          <Card style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
            {!selectedTab ? (
              <MissionGeneralEdit />
            ) : (
              <MissionTranslationsEdit itemIndex={selectedTab - 1} />
            )}
          </Card>
        </Stack>
      </Grow>
      <LoadingBackdrop open={controllerLoading} />
      {/* add translation dialog */}
      <Dialog onClose={handleAddTranslationClose} open={addTranslationOpen}>
        <DialogTitle>Add translation</DialogTitle>
        {translationToAdd ? (
          <DialogContent>
            <DialogContentText id="alert-dialog-description-add-translation">
              Are you sure you want to add{" "}
              <b>{translationToAdd.label.toUpperCase()}</b>?
            </DialogContentText>
          </DialogContent>
        ) : languagesForEpisodeTabs.filter((item) => {
            if (
              item.value !== "general" &&
              !currentMission.document.items.some(
                (translationItem) => translationItem.lang === item.value
              )
            ) {
              return item
            }
          }).length ? (
          <List sx={{ pt: 0, minWidth: 300 }}>
            {languagesForEpisodeTabs
              .filter((item) => {
                if (
                  item.value !== "general" &&
                  !currentMission.document.items.some(
                    (translationItem) => translationItem.lang === item.value
                  )
                ) {
                  return item
                }
              })
              .map((item) => (
                <ListItem key={item.value} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      setTranslationToAdd(item)
                    }}
                  >
                    <Stack direction="row" spacing={1}>
                      <span>{item.flag}</span>
                      <span>{item.label.toUpperCase()}</span>
                    </Stack>
                  </ListItemButton>
                </ListItem>
              ))}
          </List>
        ) : (
          <DialogContent>
            <DialogContentText id="alert-dialog-description-add-translation">
              You have already added all languages available
            </DialogContentText>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={handleAddTranslationClose}>Cancel</Button>
          {translationToAdd && (
            <Button
              onClick={() => {
                addTranslation(translationToAdd.value)
                handleAddTranslationClose()
                setSelectedTab(currentMission.document.items.length)
              }}
            >
              Add {translationToAdd.label}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {/* no default translation alert dialog */}
      <CantPerformActionDialog
        open={noDefaultTranslationAlertDialogOpen}
        setOpen={setNoDefaultTranslationAlertDialogOpen}
        content="There must be a default translation"
      />
      {/* suspend mission dialog */}
      <Dialog open={suspendDialogOpen} TransitionComponent={DialogTransition}>
        <DialogTitle>
          Are you sure you want to{" "}
          {currentMission.suspended ? "resume" : "suspend"} this Mission?
        </DialogTitle>
        <DialogContent>
          <Typography>
            {currentMission.stage !== Stage.PUBLISHED
              ? "All the changes will be published and "
              : ""}
            {currentMission.stage !== Stage.PUBLISHED ? "this Mission" : "It"}{" "}
            {currentMission.suspended
              ? "will be assigned to users again"
              : "will no longer be assigned to users"}
            .
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={controllerLoading}
            onClick={() => {
              setSuspendDialogOpen(false)
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={controllerLoading}
            onClick={async () => {
              setSuspendDialogOpen(false)
              setControllerLoading(true)

              let noErrors: boolean

              noErrors = await upsertMissionParent(
                currentMission.suspended ? false : true
              )
              if (noErrors) {
                noErrors = await publishMission()
                if (noErrors) {
                  setChangesSaved(true)
                }
              }

              setControllerLoading(false)
            }}
          >
            {currentMission.suspended ? "Resume" : "Suspend"}
          </Button>
        </DialogActions>
      </Dialog>
    </EditContainer>
  )
}

export default MissionEdit
